/* eslint-disable react-hooks/exhaustive-deps */
import {useEffect, useState} from 'react';
import {Box, Grid, Link, Skeleton} from '@mui/material';
import {shallowEqual, useSelector} from 'react-redux';
import useLoadAds from '@jobseeker/common-core/hooks/useLoadAds';
import useInterceptor from '@jobseeker/common-core/hooks/useInterceptor';
import {Image} from '@jobseeker/lotus/components/Image';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks/common';
import bannerAdsManifest from '../generated/bannerAdsManifest.json';

import type {AdsElementType} from '@jobseeker/common-core/types';
import type {AppState} from '@jobseeker/common-core/types';
import {
  getCompanyNameFromShowcase,
  getKeyFromPath,
  parseShowcaseUrl,
  TrackEventUBA,
  getUbaPageName,
  openUrl,
  deferTask,
} from '@jobseeker/common-core/utils';

// Build-time snapshot of banner ad images (see scripts/prefetch-showcase-media.mjs).
// Maps the live billboard CDN url -> a local /showcase-media/... copy that ships
// in public/. We serve the local same-origin copy when present and fall back to
// the live url otherwise (e.g. a new ad added between deploys).
const adImageManifest = bannerAdsManifest as Record<string, string>;
const resolveAdImage = (url?: string): string =>
  (url && adImageManifest[url]) || url || '';

// True when resolveAdImage returned a local snapshot path (not the live url).
// Snapshots must be served same-origin from the app's public/showcase-media, so
// we render them with useCdn={false} to bypass the Image component's production
// CDN rewrite (which would otherwise point at js-static.* cross-origin).
const isSnapshot = (url?: string): boolean =>
  !!url && resolveAdImage(url) !== url;

const CompanyGallery = () => {
  const [adsContent, setAdsContent] = useState(Array<AdsElementType>);
  // Tracks per-image load status so a skeleton stays visible until the
  // actual banner image (not just the ad data) finishes downloading, and so
  // a failed image can be hidden instead of leaving a blank box.
  const [imageStatus, setImageStatus] = useState<
    Record<number, 'loaded' | 'error'>
  >({});
  // Indices whose snapshotted /showcase-media copy failed to load and have been
  // swapped back to the original live url. Lets a missing/404 snapshot self-heal
  // instead of leaving a blank box (the manifest lookup itself can't detect a
  // 404 -- it only knows whether the url was snapshotted, not whether the file
  // is actually served).
  const [liveFallback, setLiveFallback] = useState<Record<number, boolean>>({});
  // Same self-healing fallback for the single company-logo image.
  const [logoLiveFallback, setLogoLiveFallback] = useState(false);

  const markImage = (index: number, status: 'loaded' | 'error') =>
    setImageStatus(prev => ({...prev, [index]: status}));

  // Resolve the src for an image, honoring a prior live-url fallback for this
  // index. Returns the local snapshot first; once it has errored we use the
  // original live url.
  const srcForImage = (index: number, liveUrl?: string): string =>
    liveFallback[index] ? liveUrl || '' : resolveAdImage(liveUrl);

  // On load error: if we were showing the local snapshot and a different live
  // url exists, swap to the live url and retry. If the live url also fails (or
  // there was no snapshot to begin with) mark it errored so the skeleton stays.
  const handleImageError = (index: number, liveUrl?: string) => {
    const usingSnapshot =
      !liveFallback[index] && !!liveUrl && resolveAdImage(liveUrl) !== liveUrl;
    if (usingSnapshot) {
      setLiveFallback(prev => ({...prev, [index]: true}));
      // Clear any error state so the skeleton lifts when the live image loads.
      setImageStatus(prev => {
        const next = {...prev};
        delete next[index];
        return next;
      });
    } else {
      markImage(index, 'error');
    }
  };

  const categoryId = useSelector(
    (state: AppState) => state.app.context.navigation.actualCategory,
    shallowEqual
  )?.toString();

  const tagId = useSelector(
    (state: AppState) => state.app.context.navigation.tag,
    shallowEqual
  );
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const interceptor: any = useInterceptor();
  const {
    page,
    ads: {
      showCase: {slot},
      jobfeedShowCase: {slot: slotJobfeed},
    },
  } = interceptor;

  const trackEvent = () => {
    const emp = adsContent[0];

    TrackEventUBA('companyBannerClick', 'click', {
      ref: getKeyFromPath('ref') || '',
      categoryID: page === 'category' ? categoryId : undefined,
      tagID: page === 'tag' ? tagId : undefined,
      page: getUbaPageName(),
      sessionUsername: sessionStorage.getItem('userId') || '',
      companyID: emp?.companyId || parseShowcaseUrl(emp?.link) || '',
      companyName: emp?.title || getCompanyNameFromShowcase(emp?.link) || '',
    });
  };

  useEffect(() => {
    if (!adsContent?.length) {
      return;
    }
    // Fresh ad set -> reset per-image load tracking and any live-url fallbacks.
    setImageStatus({});
    setLiveFallback({});
    setLogoLiveFallback(false);
    const employer = adsContent[0];

    TrackEventUBA('companyBannerView', 'view', {
      ref: getKeyFromPath('ref') || '',
      categoryID: page === 'category' ? categoryId : undefined,
      tagID: page === 'tag' ? tagId : undefined,
      page: getUbaPageName(),
      sessionUsername: sessionStorage.getItem('userId') || '',
      companyID: employer?.companyId || parseShowcaseUrl(employer?.link) || '',
      companyName:
        employer?.title || getCompanyNameFromShowcase(employer?.link) || '',
    });
  }, [adsContent]);

  const regex = /^([a-z0-9-&]+)-jobs(?:-in-([a-z]+))?$/;

  const matchedForCityPage = page?.match(regex);

  useLoadAds(
    page === 'jobfeed' || !!matchedForCityPage ? slotJobfeed : slot,
    setAdsContent,
    page === 'jobfeed' || !!matchedForCityPage
      ? {}
      : {
          categoryIds: parseInt(categoryId ?? '0') > 0 ? categoryId : 5,
        },
    false
  );

  const isAboveMobile = useIsLargeDevice('md');

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: {xxs: 160, md: 200},
        height: {xxs: 160, md: 200},
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {adsContent?.length > 0 ? (
        <>
          {' '}
          <Grid
            container
            sx={{
              minHeight: {xxs: 160, md: 200},
              height: {xxs: 160, md: 240},
              flexWrap: 'nowrap',
              justifyContent: 'center',
            }}
          >
            <a
              onClick={e => {
                e.preventDefault();
                deferTask(() => {
                  trackEvent();
                  openUrl(adsContent[0]?.link || '/', '_blank');
                });
              }}
              href={adsContent[0]?.link}
              target="__blank"
              style={{
                textDecoration: 'none',
                display: 'flex',
                width: '100%',
              }}
            >
              {adsContent?.map((adItem, index) => (
                <Grid
                  item
                  key={index}
                  sx={{
                    flexShrink: 0,
                    overflow: 'hidden',
                    width: {xxs: '100%', md: `${100 / adsContent.length}%`},
                    px: '1px',
                  }}
                >
                  <Box
                    sx={{
                      overflow: 'hidden', // clip the image
                      width: '100%',
                      height: '100%',
                      position: 'relative',
                    }}
                  >
                    {/* Keep a skeleton behind each image until it actually
                        downloads. The ad JSON arrives fast on slow networks
                        while these banner images are still in flight, so
                        without this the boxes render blank and look broken. */}
                    {imageStatus[index] !== 'loaded' && (
                      <Skeleton
                        variant="rounded"
                        width={'100%'}
                        height={'100%'}
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          borderRadius: 2,
                        }}
                      />
                    )}
                    <Box
                      component={Image}
                      src={srcForImage(index, adItem?.imageUrl)}
                      // Serve the same-origin snapshot directly; skip the CDN
                      // rewrite. Once fallen back to the live url, use default.
                      useCdn={
                        !liveFallback[index] && isSnapshot(adItem?.imageUrl)
                          ? false
                          : undefined
                      }
                      alt={`Office ${index + 1}`}
                      loading="lazy"
                      // Decode off the main thread so a large banner doesn't
                      // block paint of the rest of the page.
                      decoding="async"
                      // These banners are decorative — keep them from competing
                      // with the listing's critical requests on a slow link.
                      fetchPriority="low"
                      onLoad={() => markImage(index, 'loaded')}
                      onError={() => handleImageError(index, adItem?.imageUrl)}
                      sx={{
                        width: '100%',
                        overflow: 'hidden', // crucial to clip the zoomed image
                        height: '100%',
                        objectFit: 'cover',
                        position: 'relative',
                        // Hide the broken-image glyph if a load fails; the
                        // skeleton stays in place so the area never looks broken.
                        opacity: imageStatus[index] === 'loaded' ? 1 : 0,
                        transition:
                          'opacity 0.3s ease-in-out, transform 0.3s ease-in-out',
                        cursor: 'pointer',
                        '&:hover': {
                          transform: 'scale(1.05) !important;',
                          transition:
                            '-ms-transform 450ms cubic-bezier(0.645, 0.045, 0.355, 1) 0s, -webkit-transform 450ms cubic-bezier(0.645, 0.045, 0.355, 1) 0s, transform 450ms cubic-bezier(0.645, 0.045, 0.355, 1) 0s !important;',
                        },
                      }}
                    />
                  </Box>
                </Grid>
              ))}
            </a>
          </Grid>
          {/* Overlay Section */}
          <Box
            display={{xxs: 'flex', md: 'grid'}}
            sx={{
              position: {xxs: 'relative', md: 'absolute'},
              bottom: {xxs: 98, md: 22},
              right: {xxs: 22, md: 20},
              textAlign: 'center',
              justifyContent: {xxs: 'space-between', md: 'center'},
              alignItems: 'end',
            }}
          >
            <Image
              src={
                logoLiveFallback
                  ? adsContent[0]?.companyLogo || ''
                  : resolveAdImage(adsContent[0]?.companyLogo)
              }
              // Same-origin snapshot -> skip the CDN rewrite (see banner image).
              useCdn={
                !logoLiveFallback && isSnapshot(adsContent[0]?.companyLogo)
                  ? false
                  : undefined
              }
              alt={'showcase_company'}
              onError={() => {
                const logo = adsContent[0]?.companyLogo;
                // Swap to the live url once if the snapshot copy failed to load.
                if (
                  !logoLiveFallback &&
                  logo &&
                  resolveAdImage(logo) !== logo
                ) {
                  setLogoLiveFallback(true);
                }
              }}
              width={isAboveMobile ? 112 : 80}
              height={isAboveMobile ? 112 : 80}
              style={{
                marginLeft: isAboveMobile ? 20 : 45,
                maxWidth: isAboveMobile ? 112 : 80,
                maxHeight: isAboveMobile ? 112 : 80,
                position: 'relative',
                objectFit: 'contain',
              }}
            />
            <Link
              onClick={e => {
                e.preventDefault();
                deferTask(() => {
                  trackEvent();
                  openUrl(adsContent[0]?.link || '/', '_blank');
                });
              }}
              href={adsContent[0]?.link}
              target="_blank"
              sx={{
                color: '#000',
                height: {xxs: '28px', md: 'auto'},
                textTransform: 'none',
                p: {xxs: '6px 13px 11px', md: '11px 13px'},
                borderRadius: 1,
                fontSize: 12,
                mt: {xxs: 0, md: 1.5},
                fontWeight: 500,
                backgroundColor: '#fff',
                boxShadow: '0 4px 11px 0 rgba(0,0,0,.39)',
                transition: 'all .3s',
                textDecoration: 'none',
                ':hover': {
                  boxShadow: 'none',
                },
                cursor: 'pointer',
              }}
            >
              VISIT COMPANY PAGE
            </Link>
          </Box>
        </>
      ) : (
        <Grid
          container
          sx={{
            minHeight: {xxs: 160, md: 220},
            height: {xxs: 120, md: 220},
            flexWrap: 'nowrap',
            justifyContent: 'center',
            gap: 0.5,
          }}
        >
          {[...new Array(isAboveMobile ? 3 : 1)].map((elem, index) => (
            <Skeleton
              key={`skele_${index}`}
              variant="rounded"
              width={'100%'}
              height={'100%'}
              sx={{
                borderRadius: 2,
              }}
            />
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default CompanyGallery;
